import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Building2, Plus, Search, Users, FileText, Trash2, Eye, AlertCircle, Loader2, CheckCircle2, UserPlus, MoreHorizontal, Power, PowerOff, Phone, MapPin, Calendar, Server, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function SuperAdminTenantsPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailTenant, setDetailTenant] = useState(null);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await axios.get(API + '/super/tenants', { headers: getSuperAdminHeader() });
      setTenants(response.data);
    } catch (err) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  const handleToggleActive = async (tenantId, currentStatus) => {
    try {
      await axios.put(API + '/super/tenants/' + tenantId, { is_active: !currentStatus }, { headers: getSuperAdminHeader() });
      toast.success(currentStatus ? 'Empresa desativada' : 'Empresa ativada');
      fetchTenants();
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDelete = async (tenantId) => {
    if (!window.confirm('Excluir empresa e todos os dados permanentemente?')) return;
    try {
      await axios.delete(API + '/super/tenants/' + tenantId, { headers: getSuperAdminHeader() });
      toast.success('Empresa excluída');
      fetchTenants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleImpersonate = async (tenant) => {
    try {
      const res = await axios.post(API + '/super/impersonate/' + tenant.id, {}, { headers: getSuperAdminHeader() });
      const { token, user, tenant: tenantData } = res.data;
      // Store in localStorage matching Zustand persist format (store name: 'oneview-auth')
      localStorage.setItem('oneview-auth', JSON.stringify({
        state: { token, user, tenant: tenantData, isAuthenticated: true },
        version: 0
      }));
      toast.success(`Acessando como ${user.full_name} em ${tenantData.company_name}`);
      window.open('/dashboard', '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao acessar tenant');
    }
  };

  const filteredTenants = tenants.filter(t => t.company_name.toLowerCase().includes(searchTerm.toLowerCase()) || (t.cnpj && t.cnpj.includes(searchTerm)));

  return (
    <div className="space-y-6" data-testid="super-admin-tenants">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Empresas</h2>
          <p className="text-gray-500">Gerencie todas as empresas cadastradas na plataforma</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-2" />Nova Empresa</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Cadastrar Nova Empresa</DialogTitle></DialogHeader>
            <CreateTenantForm onSuccess={() => { setCreateDialogOpen(false); fetchTenants(); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Buscar por nome ou CNPJ..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <Badge variant="secondary" className="bg-gray-100 text-gray-700">{filteredTenants.length} empresas</Badge>
      </div>

      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Empresa</TableHead>
                  <TableHead className="font-semibold text-gray-700">CNPJ</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-center">Usuários</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-center">Relatórios</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Zabbix</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-400">
                      <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />Nenhuma empresa encontrada
                    </TableCell>
                  </TableRow>
                )}
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{tenant.company_name}</p>
                          <p className="text-xs text-gray-500">{new Date(tenant.created_at).toLocaleDateString('pt-BR')}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">{tenant.cnpj || '-'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700"><Users className="h-3 w-3 mr-1" />{tenant.user_count}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="bg-purple-50 text-purple-700"><FileText className="h-3 w-3 mr-1" />{tenant.report_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch checked={tenant.is_active !== false} onCheckedChange={() => handleToggleActive(tenant.id, tenant.is_active !== false)} />
                        <span className={tenant.is_active !== false ? 'text-sm text-green-600' : 'text-sm text-gray-400'}>{tenant.is_active !== false ? 'Ativo' : 'Inativo'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {tenant.has_zabbix_config ? (
                        <Badge className="bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="h-3 w-3 mr-1" />Configurado</Badge>
                      ) : (
                        <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200"><AlertCircle className="h-3 w-3 mr-1" />Pendente</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailTenant(tenant)}><Eye className="h-4 w-4 mr-2" />Ver Detalhes</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/tenant-super/tenants/${tenant.id}`)}><Server className="h-4 w-4 mr-2" />Métricas & Incidentes</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleImpersonate(tenant)}><LogIn className="h-4 w-4 mr-2" />Acessar como Cliente</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(tenant.id, tenant.is_active !== false)}>
                            {tenant.is_active !== false ? <><PowerOff className="h-4 w-4 mr-2" />Desativar</> : <><Power className="h-4 w-4 mr-2" />Ativar</>}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(tenant.id)} className="text-red-600 focus:text-red-600"><Trash2 className="h-4 w-4 mr-2" />Excluir</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {detailTenant && <TenantDetailDialog tenant={detailTenant} onClose={() => setDetailTenant(null)} onRefresh={fetchTenants} />}
    </div>
  );
}

function CreateTenantForm({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ company_name: '', cnpj: '', phone: '', address: '', admin_email: '', admin_password: '', admin_name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(API + '/super/tenants', formData, { headers: getSuperAdminHeader() });
      toast.success('Empresa cadastrada com sucesso');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2"><Label>Nome da Empresa *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} required /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>CNPJ</Label><Input value={formData.cnpj} onChange={(e) => setFormData({...formData, cnpj: e.target.value})} placeholder="00.000.000/0000-00" /></div>
        <div className="space-y-2"><Label>Telefone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
      </div>
      <div className="space-y-2"><Label>Endereço</Label><Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} /></div>
      <div className="border-t pt-4">
        <h4 className="font-medium mb-3 text-gray-900">Administrador da Empresa</h4>
        <div className="space-y-3">
          <div className="space-y-2"><Label>Nome Completo *</Label><Input value={formData.admin_name} onChange={(e) => setFormData({...formData, admin_name: e.target.value})} required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formData.admin_email} onChange={(e) => setFormData({...formData, admin_email: e.target.value})} required /></div>
            <div className="space-y-2"><Label>Senha *</Label><Input type="password" value={formData.admin_password} onChange={(e) => setFormData({...formData, admin_password: e.target.value})} required /></div>
          </div>
        </div>
      </div>
      <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading}>{loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Cadastrando...</> : 'Cadastrar Empresa'}</Button>
    </form>
  );
}

function TenantDetailDialog({ tenant, onClose, onRefresh }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addUserOpen, setAddUserOpen] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await axios.get(API + '/super/tenants/' + tenant.id, { headers: getSuperAdminHeader() });
        setDetails(response.data);
      } catch (err) {
        toast.error('Erro ao carregar detalhes');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [tenant.id]);

  const handleToggleUserActive = async (userId, currentStatus) => {
    try {
      await axios.put(API + '/super/users/' + userId, { is_active: !currentStatus }, { headers: getSuperAdminHeader() });
      toast.success(currentStatus ? 'Usuário desativado' : 'Usuário ativado');
      const response = await axios.get(API + '/super/tenants/' + tenant.id, { headers: getSuperAdminHeader() });
      setDetails(response.data);
    } catch (err) {
      toast.error('Erro ao atualizar usuário');
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center"><Building2 className="h-5 w-5 text-indigo-600" /></div>
            <div><span className="text-xl">{tenant.company_name}</span><p className="text-sm text-gray-500 font-normal">{tenant.cnpj || 'Sem CNPJ'}</p></div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-6 space-y-4"><Skeleton className="h-20 w-full" /><Skeleton className="h-32 w-full" /></div>
        ) : details && (
          <Tabs defaultValue="info" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="info">Informações</TabsTrigger>
              <TabsTrigger value="users">Usuários ({details.users.length})</TabsTrigger>
              <TabsTrigger value="config">Configurações</TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              <TabsContent value="info" className="mt-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Card><CardContent className="p-4 text-center"><Users className="h-8 w-8 mx-auto text-blue-500 mb-2" /><p className="text-2xl font-bold">{details.metrics.total_users}</p><p className="text-sm text-gray-500">Usuários</p></CardContent></Card>
                  <Card><CardContent className="p-4 text-center"><FileText className="h-8 w-8 mx-auto text-purple-500 mb-2" /><p className="text-2xl font-bold">{details.metrics.total_reports}</p><p className="text-sm text-gray-500">Relatórios</p></CardContent></Card>
                </div>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Informações de Contato</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-gray-400" /><span>{details.tenant.phone || 'Não informado'}</span></div>
                    <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-400" /><span>{details.tenant.address || 'Não informado'}</span></div>
                    <div className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gray-400" /><span>Cadastrado em {new Date(details.tenant.created_at).toLocaleDateString('pt-BR')}</span></div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="users" className="mt-0 space-y-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">{details.users.length} usuário(s) cadastrado(s)</p>
                  <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
                    <DialogTrigger asChild><Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"><UserPlus className="h-4 w-4 mr-2" />Adicionar Usuário</Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Adicionar Usuário</DialogTitle></DialogHeader>
                      <AddUserForm tenantId={tenant.id} onSuccess={async () => { setAddUserOpen(false); const response = await axios.get(API + '/super/tenants/' + tenant.id, { headers: getSuperAdminHeader() }); setDetails(response.data); }} />
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="space-y-2">
                  {details.users.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-medium">{user.full_name.charAt(0).toUpperCase()}</div>
                        <div><p className="font-medium text-gray-900">{user.full_name}</p><p className="text-xs text-gray-500">{user.email}</p></div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary">{user.role}</Badge>
                        <div className="flex items-center gap-2">
                          <Switch checked={user.is_active !== false} onCheckedChange={() => handleToggleUserActive(user.id, user.is_active !== false)} />
                          <span className={user.is_active !== false ? 'text-xs text-green-600' : 'text-xs text-gray-400'}>{user.is_active !== false ? 'Ativo' : 'Inativo'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="config" className="mt-0 space-y-4">
                <Card>
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Server className="h-4 w-4" />Configurações Zabbix</CardTitle></CardHeader>
                  <CardContent>
                    {details.zabbix_configs.length === 0 ? (
                      <p className="text-gray-400 text-center py-4">Nenhuma configuração Zabbix</p>
                    ) : (
                      <div className="space-y-2">
                        {details.zabbix_configs.map(config => (
                          <div key={config.id} className="p-3 rounded-lg border">
                            <p className="font-medium">{config.name}</p>
                            <p className="text-xs text-gray-500 truncate">{config.url}</p>
                            <Badge className={config.is_active ? 'mt-2 text-xs bg-green-50 text-green-700' : 'mt-2 text-xs bg-gray-100 text-gray-600'}>{config.is_active ? 'Ativo' : 'Inativo'}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddUserForm({ tenantId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ full_name: '', email: '', password: '', role: 'user' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(API + '/super/tenants/' + tenantId + '/users', formData, { headers: getSuperAdminHeader() });
      toast.success('Usuário adicionado');
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao adicionar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
      <div className="space-y-2"><Label>Nome Completo *</Label><Input value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required /></div>
      <div className="space-y-2"><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required /></div>
      <div className="space-y-2"><Label>Senha *</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required /></div>
      <div className="space-y-2">
        <Label>Função</Label>
        <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} className="w-full border rounded-md px-3 py-2">
          <option value="user">Usuário</option>
          <option value="admin">Administrador</option>
        </select>
      </div>
      <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700" disabled={loading}>{loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adicionando...</> : 'Adicionar Usuário'}</Button>
    </form>
  );
}
